import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { brandColors } from '../../theme';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Fade,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import { useMarketBySlug } from '../../hooks/useMarketCompassApi';
import { SimplePriceSelector } from '../../components/cart/SimplePriceSelector';
import { SimpleAddToCartButton } from '../../components/cart/SimpleAddToCartButton';
import { useCart } from '../../hooks/useCart';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { usePublicFetch } from '../../hooks/usePublicFetch';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../utils/config';
import { MarketReport, MarketPodcast } from '@horwath-htl/shared-types';
import { Headphones } from '@mui/icons-material';
import { SimplePodcastPlayer } from '../../components/podcast';
import { SEO } from '../../components/SEO';
import { generateProductSchema, generateBreadcrumbSchema, generateLocalBusinessSchema } from '../../components/SEO/schemas';

const MarketDetail: React.FC = () => {
  const { continentName, countryName, marketName } = useParams<{ 
    continentName: string;
    countryName: string;
    marketName: string;
  }>();
  const { navigateTo } = useAppNavigation();
  const location = useLocation();
  const { addItem, isItemInCart, itemCount } = useCart();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const { isAuthenticated } = useAuth();
  const [selectedPrice, setSelectedPrice] = useState<any>(null);

  const { 
    data: market, 
    isLoading: loadingDetails, 
    error: detailsError 
  } = useMarketBySlug(continentName, countryName, marketName);
  
  // Debug market data
  useEffect(() => {
    if (market) {
      console.log('Market data:', market);
      console.log('Market has podcast?', (market as any).podcast);
      console.log('Market has podcasts?', (market as any).podcasts);
    }
  }, [market]);

  // Fetch reports for the market - use public endpoint for insights
  const { data: reportsData, refetch: refetchReports } = useQuery({
    queryKey: ['market-reports-store', market?.id, isAuthenticated],
    queryFn: async () => {
      if (!market?.id) return null;
      
      // For authenticated users, try to get full report data
      // For unauthenticated users, get public insights only
      const response = isAuthenticated 
        ? await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${market.id}/reports`)
        : await publicFetch(`${API_URL}/api/store/markets/${market.id}/report-insights`);
      
      if (!response || !response.ok) {
        // Fallback to public insights endpoint if marketcompass fails
        if (isAuthenticated) {
          const fallbackResponse = await publicFetch(`${API_URL}/api/store/markets/${market.id}/report-insights`);
          if (fallbackResponse && fallbackResponse.ok) {
            const result = await fallbackResponse.json();
            return result.data || result;
          }
        }
        return null;
      }
      
      const result = await response.json();
      // Normalize the response structure
      if (result.data) {
        return result.data;
      }
      return result;
    },
    enabled: !!market?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes for all users
    gcTime: 10 * 60 * 1000, // 10 minutes for all users
  });

  // Fetch podcasts for the market - only for authenticated users
  const { data: podcastsData, refetch: refetchPodcasts } = useQuery({
    queryKey: ['market-podcasts-store', market?.id, isAuthenticated],
    queryFn: async () => {
      if (!market?.id || !isAuthenticated) return null;
      
      console.log('Fetching podcasts for market:', market.id, 'isAuthenticated:', isAuthenticated);
      
      // Try marketcompass endpoint for authenticated users
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${market.id}/podcasts`);
      
      console.log('MarketCompass podcast response:', response?.status, response?.ok);
      
      if (!response || !response.ok) {
        console.error('Failed to fetch podcasts:', response?.status);
        return null;
      }
      
      const result = await response.json();
      console.log('Podcasts data received:', result);
      return result;
    },
    enabled: !!market?.id && isAuthenticated, // Only fetch if authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Refetch reports and podcasts when authentication state changes
  useEffect(() => {
    if (isAuthenticated && market?.id) {
      // Small delay to ensure auth token is properly set
      const timer = setTimeout(() => {
        refetchReports();
        refetchPodcasts();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, market?.id, refetchReports, refetchPodcasts]);

  // Set initial price when market data loads - MUST be before any conditional returns
  useEffect(() => {
    if (market?.subscription?.prices && market.subscription.prices.length > 0 && !selectedPrice) {
      const defaultPrice = market.subscription.prices[0];
      setSelectedPrice({
        ...defaultPrice,
        productId: market.subscription.productId
      });
    }
  }, [market, selectedPrice]);

  const getMarketIcon = (): string => {
    return '🏨';
  };

  if (loadingDetails) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading market details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (detailsError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Failed to load market details. Please try again.
        </Alert>
      </Container>
    );
  }

  if (!market) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Market "{marketName}" not found.
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigateTo(`/global-market-reports/${continentName}/${countryName}`)}
          >
            Back to Markets
          </Button>
        </Box>
      </Container>
    );
  }

  // Helper function to get the latest report period
  const getLatestReportPeriod = (): string => {
    if (reportsData?.reports && reportsData.reports.length > 0) {
      // Get the latest report (first in array)
      const latestReport = reportsData.reports[0] as MarketReport;
      
      // Try to use reportMonth first if available
      if (latestReport.reportMonth) {
        const year = Math.floor(latestReport.reportMonth / 100);
        const month = latestReport.reportMonth % 100;
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      
      // Fallback to publishedAt if reportMonth is not available
      if (latestReport.publishedAt) {
        const date = new Date(latestReport.publishedAt);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
    }
    
    // If no reports, use current date minus 50 days
    const fallbackDate = new Date();
    fallbackDate.setDate(fallbackDate.getDate() - 50);
    return fallbackDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Generate breadcrumb items for structured data
  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Global Market Reports', url: '/global-market-reports' },
    { name: continentName?.replace(/-/g, ' ') || '', url: `/global-market-reports/${continentName}` },
    { name: countryName?.replace(/-/g, ' ') || '', url: `/global-market-reports/${continentName}/${countryName}` },
    { name: market.name, url: `/global-market-reports/${continentName}/${countryName}/${marketName}` }
  ];

  // Generate product schema
  const productSchema = market.subscription?.prices?.[0] ? 
    generateProductSchema(
      market.name,
      `Comprehensive quarterly hospitality market analysis for ${market.name}. Includes KPIs, supply analysis, demand trends, ADR, RevPAR, occupancy rates, and strategic forecasts.`,
      market.subscription.prices[0].amount / 100
    ) : null;

  // Generate local business schema
  const localBusinessSchema = generateLocalBusinessSchema(
    market.name,
    `Professional hospitality market intelligence and consulting services for ${market.name} hotels and tourism industry.`
  );

  // Combine schemas
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateBreadcrumbSchema(breadcrumbItems),
      ...(productSchema ? [productSchema] : []),
      localBusinessSchema
    ]
  };

  return (
    <>
      <SEO
        title={`${market.name} Hotel Market Report & Analysis`}
        description={`Latest hospitality market intelligence for ${market.name}. Quarterly performance metrics, ADR, RevPAR, occupancy rates, supply pipeline, demand trends, and strategic insights from Horwath HTL experts.`}
        keywords={`${market.name} hotels, ${market.name} hospitality market, ${market.name} tourism, ${market.name} ADR, ${market.name} RevPAR, ${market.name} occupancy, hotel analytics, market reports`}
        canonical={`/global-market-reports/${continentName}/${countryName}/${marketName}`}
        type="article"
        structuredData={structuredData}
      />
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
              mb: 2,
            }}
          >
            {market.name} MarketCompass Report
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Comprehensive market analysis and insights
          </Typography>
        </Box>

      {/* Breadcrumbs */}
      <AppBreadcrumbs
        items={[
          { label: 'Continents', path: '/global-market-reports' },
          { label: market.continent?.name || 'Unknown Continent', path: `/global-market-reports/${continentName}` },
          { label: market.country?.name || 'Unknown Country', path: `/global-market-reports/${continentName}/${countryName}` },
          { label: market.name }
        ]}
        sx={{ mb: 3 }}
      />

      {/* Market Details */}
      <Fade in={true}>
        <Card>
          <CardContent>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                {market.productImage ? (
                  <Box sx={{ maxWidth: 300, width: '100%' }}>
                    <img 
                      src={market.productImage} 
                      alt={market.name}
                      style={{ 
                        width: '100%', 
                        height: 'auto',
                        display: 'block',
                        borderRadius: '8px'
                      }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Typography variant="h1">{getMarketIcon()}</Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Typography variant="h3" fontWeight="bold" gutterBottom>
                      {market.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip label={`Latest Report: ${getLatestReportPeriod()}`} color="primary" />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      Market Analysis Report
                    </Typography>
                    <Typography variant="body1">
                      {market.description || 'Comprehensive market analysis and strategic insights for this destination.'}
                    </Typography>
                  </Grid>
                  
                  {/* Purchase Options */}
                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Select Subscription
                      </Typography>
                      {market?.subscription && market.subscription.prices && market.subscription.prices.length > 0 ? (
                        <>
                          <SimplePriceSelector
                            prices={market.subscription.prices}
                            onPriceSelect={(price) => {
                              setSelectedPrice({
                                ...price,
                                productId: market.subscription?.productId || ''
                              });
                            }}
                          />
                          <SimpleAddToCartButton
                            market={{ 
                              id: String(market.id), 
                              name: market.name 
                            }}
                            selectedPrice={selectedPrice}
                            size="large"
                            fullWidth
                            hidePrice={true}
                            isInCart={isItemInCart(String(market.id))}
                            onAddToCart={(item) => {
                              addItem(item);
                            }}
                          />
                          {itemCount > 0 && (
                            <Button
                              variant="outlined"
                              size="large"
                              fullWidth
                              onClick={() => navigateTo('/checkout')}
                              sx={{ mt: 1 }}
                            >
                              Proceed to Checkout
                            </Button>
                          )}
                        </>
                      ) : (
                        <Alert severity="info">
                          Pricing information is not available for this market.
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Report Description */}
            {reportsData?.reports?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Paper sx={{ p: 3 }}>
                  {/* Quick Report Insights from latest report */}
                  {reportsData?.reports && reportsData.reports.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Quick Report Insights
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {(reportsData.reports[0] as MarketReport).longDescription}
                      </Typography>
                    </>
                  )}
                  
                  {/* Disclaimer for markets without recent reports */}
                  {(() => {
                    const threeMonthsAgo = new Date();
                    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                    
                    const hasRecentReport = reportsData?.reports?.some((report: MarketReport) => {
                      if (!report.publishedAt) return false;
                      const reportDate = new Date(report.publishedAt);
                      return reportDate >= threeMonthsAgo;
                    });
                    
                    if (!hasRecentReport) {
                      return (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Note:</strong> After purchasing your subscription, the MarketCompass report will be generated and available within 1 business day. You will receive an email notification when your report is ready to download.
                          </Typography>
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                </Paper>
              </Box>
            )}

            {/* Podcast Player - Only for authenticated users */}
            {isAuthenticated && (() => {
              console.log('Podcast display check:', {
                podcastsData,
                hasPodcasts: podcastsData?.podcasts,
                podcastsLength: podcastsData?.podcasts?.length,
                shouldDisplay: podcastsData?.podcasts && podcastsData.podcasts.length > 0,
                marketPodcast: (market as any)?.podcast,
                marketPodcasts: (market as any)?.podcasts
              });
              
              // Check if market data already includes podcast
              const marketPodcast = (market as any)?.podcast || ((market as any)?.podcasts && (market as any).podcasts[0]);
              
              // Check if we have podcasts from the separate fetch
              const podcasts = podcastsData?.podcasts || podcastsData;
              
              // Use market podcast if available, otherwise use fetched podcasts
              const podcastToPlay = marketPodcast || (Array.isArray(podcasts) && podcasts.length > 0 ? podcasts[0] : null);
              
              if (podcastToPlay) {
                console.log('Playing podcast:', podcastToPlay);
                return (
                  <Box sx={{ mb: 3 }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Headphones sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6">
                            Listen to a short podcast
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Get quick insights about this market
                        </Typography>
                        <SimplePodcastPlayer 
                          podcast={{ 
                            ...podcastToPlay, 
                            marketId: podcastToPlay.marketId || parseInt(market.id) 
                          }} 
                        />
                      </CardContent>
                    </Card>
                  </Box>
                );
              }
              return null;
            })()}

            {/* Login Prompt for Podcast */}
            {!isAuthenticated && !podcastsData && (
              <Box sx={{ mb: 3 }}>
                <Paper sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Unlock Podcast Insights
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Create a free account or login to access:
                  </Typography>
                  <Box sx={{ pl: 2, mb: 2 }}>
                    <Typography variant="body2">
                      • Short podcast discussing this market's opportunities and trends
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                    Creating an account only requires an email address - no credit card needed!
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      onClick={() => navigateTo('/login', { state: { from: location.pathname + location.search } })}
                      sx={{ 
                        backgroundColor: 'white',
                        color: '#667eea',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}
                    >
                      Login / Sign Up
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Report Sections */}
            {market.reportSections && market.reportSections.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Report Contents
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  This comprehensive report contains 17 sections covering all aspects of the hotel market:
                </Typography>
                <Grid container spacing={2}>
                  {market.reportSections.map((category: any, idx: number) => (
                    <Grid item xs={12} key={idx}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {category.category}
                        </Typography>
                        {category.categoryDescription && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                            {category.categoryDescription}
                          </Typography>
                        )}
                        <Box sx={{ pl: 2 }}>
                          {category.sections.map((section: any, sIdx: number) => (
                            <Box key={sIdx} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                              <Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {section.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {section.description}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            
            {/* Related Markets Section */}
            {market.relatedMarkets && market.relatedMarkets.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                  Related Markets
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {(() => {
                    // Randomly select up to 12 markets
                    const shuffled = [...market.relatedMarkets].sort(() => 0.5 - Math.random());
                    const selected = shuffled.slice(0, 12);
                    
                    return selected.map((relatedMarket: any) => (
                      <Chip
                        key={relatedMarket.id}
                        label={relatedMarket.name}
                        variant="outlined"
                        clickable
                        onClick={() => {
                          // Navigate to the related market
                          // Use the same continent and country as the current market since related markets are typically in the same country
                          navigateTo(`/global-market-reports/${continentName}/${countryName}/${relatedMarket.slug || relatedMarket.name.toLowerCase().replace(/\s+/g, '-')}`);
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          }
                        }}
                      />
                    ));
                  })()}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Container>
    </>
  );
};

export default MarketDetail;